import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import organizationService from '../services/organizationService';

const OrganizationContext = createContext();

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null); // Add state for stats
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastLoadedOrgId, setLastLoadedOrgId] = useState(null);
  const [userEffectivePermissions, setUserEffectivePermissions] = useState(null); // Custom permissions from backend
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  // Determine user's account type and capabilities
  const accountType = currentUser?.accountType || 'individual';
  const isEnterpriseUser = accountType === 'organization';
  const isIndividualUser = accountType === 'individual';
  
  // Role-based capabilities
  const isPlatformAdmin = currentUser?.platformRole === 'super_admin' || 
                         currentUser?.platformRole === 'platform_admin';
  const isOrgAdmin = currentUser?.orgRole === 'org_admin';
  const isDeptManager = currentUser?.orgRole === 'dept_manager';
  // Default to 'full' if orgAdminAccessLevel is null or undefined (for Organization Owners)
  const orgAdminAccessLevel = isOrgAdmin ? (currentUser?.orgAdminAccessLevel || 'full') : null;
  const isReadOnlyOrgAdmin = isOrgAdmin && orgAdminAccessLevel === 'read_only';
  const isLimitedOrgAdmin = isOrgAdmin && orgAdminAccessLevel === 'limited';
  const isFullOrgAdmin = isOrgAdmin && orgAdminAccessLevel === 'full';
  
  // Department-level role capabilities
  const departmentRole = currentUser?.departmentRole;
  const isDeptManagerByRole = departmentRole === 'dept_manager';
  const isTeamLead = departmentRole === 'team_lead';
  const hasDepartmentAccess = Boolean(currentUser?.departmentId); // Has a department assigned - any user with departmentId can view their department

  // Debug logging
  useEffect(() => {
    if (currentUser) {
      console.log('👤 OrganizationContext - Current User Data:', {
        id: currentUser._id,
        name: currentUser.name,
        email: currentUser.email,
        orgRole: currentUser.orgRole,
        rawOrgAdminAccessLevel: currentUser.orgAdminAccessLevel,
        computedOrgAdminAccessLevel: orgAdminAccessLevel,
        departmentRole: currentUser.departmentRole,
        departmentId: currentUser.departmentId,
        organizationId: currentUser.organizationId,
        isOrgAdmin,
        isFullOrgAdmin,
        isReadOnlyOrgAdmin,
        isLimitedOrgAdmin,
        isDeptManagerByRole,
        hasDepartmentAccess
      });
    }
  }, [currentUser]);

  // Fetch user's effective permissions from backend
  const fetchUserEffectivePermissions = useCallback(async () => {
    if (!currentUser?._id) return;
    
    try {
      setPermissionsLoading(true);
      const data = await organizationService.getUserPermissions(currentUser._id);
      setUserEffectivePermissions(data.effectivePermissions);
      console.log('✅ Loaded effective permissions:', data.effectivePermissions);
    } catch (error) {
      console.error('Error fetching effective permissions:', error);
      // Fallback to role-based permissions
      setUserEffectivePermissions(null);
    } finally {
      setPermissionsLoading(false);
    }
  }, [currentUser?._id]);

  // Load effective permissions when user changes
  useEffect(() => {
    if (currentUser && isEnterpriseUser) {
      fetchUserEffectivePermissions();
    } else {
      setUserEffectivePermissions(null);
    }
  }, [currentUser?._id, isEnterpriseUser, fetchUserEffectivePermissions]);

  // Initialize organization context when user has organization access
  useEffect(() => {
    const organizationId = currentUser?.organizationId || null;
    const shouldLoadOrgData = isAuthenticated && currentUser && (organizationId || isPlatformAdmin);

    if (shouldLoadOrgData) {
      const cacheKey = organizationId || 'ALL_ORGS';

      if (lastLoadedOrgId !== cacheKey) {
        loadOrganizationData(organizationId); // Pass organizationId
        setLastLoadedOrgId(cacheKey);
      }
    } else {
      if (lastLoadedOrgId !== null) {
        setLastLoadedOrgId(null);
      }

      if (currentOrganization !== null) {
        setCurrentOrganization(null);
      }
      if (dashboardStats !== null) { // Clear stats
        setDashboardStats(null);
      }

      if (availableOrganizations.length > 0) {
        setAvailableOrganizations([]);
      }
    }
  }, [isAuthenticated, currentUser?.organizationId, isPlatformAdmin, lastLoadedOrgId]); // Add lastLoadedOrgId

  const loadOrganizationData = useCallback(async (organizationId) => {
    if (!organizationId) return;

    try {
      setLoading(true);
      setError(null);

      // Parallelize data fetching
      const [orgData, deptData, statsData] = await Promise.all([
        organizationService.getCurrentOrganization(),
        organizationService.getDepartments(organizationId),
        organizationService.getOrganizationDashboardStats()
      ]);

      setCurrentOrganization(orgData);
      setDepartments(deptData);
      setDashboardStats(statsData);

    } catch (err) {
      console.error('Error loading organization data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []); // Dependencies removed, as orgId is passed directly

  // Function to manually refresh data
  const refreshOrganizationData = useCallback(() => {
    const organizationId = currentUser?.organizationId;
    if (organizationId) {
      setLastLoadedOrgId(null); // Bust cache to allow reload
      loadOrganizationData(organizationId);
    }
  }, [currentUser?.organizationId, loadOrganizationData]);

  // Refresh only dashboard stats (e.g. so welcome vs full dashboard updates after creating a project/team)
  const refreshDashboardStats = useCallback(async () => {
    const organizationId = currentUser?.organizationId;
    if (!organizationId) return;
    try {
      const statsData = await organizationService.getOrganizationDashboardStats();
      setDashboardStats(statsData);
    } catch (err) {
      console.error('Error refreshing dashboard stats:', err);
    }
  }, [currentUser?.organizationId]);

  // Switch organization context (for platform admins)
  const switchOrganization = async (organizationId) => {
    if (!isPlatformAdmin) {
      throw new Error('Insufficient permissions to switch organizations');
    }

    try {
      setLoading(true);
      const orgData = await organizationService.getOrganization(organizationId);
      const deptData = await organizationService.getDepartments(organizationId);
      
      setCurrentOrganization(orgData);
      setDepartments(deptData);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Create new organization (platform admin only)
  const createOrganization = async (orgData) => {
    if (!isPlatformAdmin) {
      throw new Error('Insufficient permissions to create organizations');
    }

    try {
      setLoading(true);
      const newOrg = await organizationService.createOrganization(orgData);
      
      // Refresh available organizations
      const allOrgs = await organizationService.getAllOrganizations();
      setAvailableOrganizations(allOrgs);
      
      return newOrg;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update organization (org admin only)
  const updateOrganization = async (updates) => {
    if (!isOrgAdmin && !isPlatformAdmin) {
      throw new Error('Insufficient permissions to update organization');
    }

    try {
      setLoading(true);
      const updatedOrg = await organizationService.updateOrganization(
        currentOrganization._id, 
        updates
      );
      
      setCurrentOrganization(updatedOrg);
      return updatedOrg;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Create department (org admin only)
  const createDepartment = async (deptData) => {
    if (!isOrgAdmin && !isPlatformAdmin) {
      throw new Error('Insufficient permissions to create departments');
    }

    try {
      setLoading(true);
      const newDept = await organizationService.createDepartment(deptData);
      
      // Refresh departments
      const deptData = await organizationService.getDepartments(currentOrganization._id);
      setDepartments(deptData);
      
      return newDept;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get user's effective permissions in current context
  const getPermissions = () => {
    return {
      // Platform level
      canManageAllOrganizations: isPlatformAdmin,
      canCreateOrganizations: isPlatformAdmin,
      
      // Organization level (orgRole-based)
  canManageOrganization: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin,
  canViewOrganizationSettings: isOrgAdmin || isPlatformAdmin,
  canInviteUsers: ((isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin || isDeptManager || isDeptManagerByRole),
  canManageUsers: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin,
      
      // Department level (orgRole OR departmentRole-based)
  canManageDepartments: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin, // Only full/limited org admins can manage ALL departments
  canViewDepartments: isOrgAdmin || isPlatformAdmin || isDeptManager || hasDepartmentAccess, // Members can view their department
  canManageDepartment: isDeptManager || isDeptManagerByRole || (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin, // Can manage their specific department
      canViewUsers: isOrgAdmin || isPlatformAdmin || isDeptManager || hasDepartmentAccess,
      canViewAnalytics: isOrgAdmin || isPlatformAdmin || isDeptManager || hasDepartmentAccess,
      
      // Team level (departmentRole-based)
  canManageTeams: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin || isDeptManagerByRole || isTeamLead,
      canViewTeams: true, // All enterprise users can view teams
      
      // Project level - Only certain roles can create projects in organization
      // Based on industry standards: Admins, Managers, and Team Leads can create projects
      // Regular members/employees cannot create projects (they can only work on assigned tasks)
      canCreateProjects: isPlatformAdmin || isOrgAdmin || isDeptManager || isDeptManagerByRole || isTeamLead,
      canViewProjects: true,
  canEditProjects: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin || isDeptManagerByRole || isTeamLead,
      
      // Task level
      canViewTasks: true, // All enterprise users can view tasks
      canEditTasks: true, // Users can edit tasks assigned to them
      canCreateTasks: true,
      
      // Individual permissions (always available)
      canManageProfile: true,
      canViewDashboard: true,
      
      // Security & Compliance permissions (organization level)
      canViewSecuritySettings: isOrgAdmin || isPlatformAdmin,
      canManageSecuritySettings: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin,
      canViewAuditLogs: isOrgAdmin || isPlatformAdmin || isDeptManager || isDeptManagerByRole,
      canExportAuditLogs: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin,
      canManagePasswordPolicy: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin,
      canManageAccessControl: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin,
      canManageCompliance: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin,
      canManageMFA: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin,
      canManageSessionSettings: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin,
      canViewSecurityReports: isOrgAdmin || isPlatformAdmin || isDeptManager || isDeptManagerByRole,
      canExportSecurityReports: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin,
      
      // Billing permissions
      viewBilling: isOrgAdmin || isPlatformAdmin,
      canManageBilling: (isOrgAdmin && !isReadOnlyOrgAdmin) || isPlatformAdmin
    };
  };

  // Check if user has specific permission (with custom permissions integration)
  const hasPermission = (permission, options = {}) => {
    // For custom permission keys (e.g., 'users.view', 'departments.create'), check custom permissions
    const isCustomPermissionKey = permission.includes('.');
    
    if (userEffectivePermissions && isCustomPermissionKey) {
      // Check custom permissions for dot-notation keys (users.view, departments.create, etc.)
      const hasBasicPerm = userEffectivePermissions[permission] === true;
      if (!hasBasicPerm) return false;
      
      // Check scope if required
      if (options.requiresScope) {
        const permCategory = permission.split('.')[0]; // e.g., 'users', 'departments'
        const scopeKey = `${permCategory}_scope`;
        const userScope = userEffectivePermissions[scopeKey];
        
        // If user has 'own' scope but needs 'all', deny
        if (options.scope === 'all' && userScope !== 'all') {
          return false;
        }
        
        // Check department match for 'own' scope
        if (userScope === 'own' && options.departmentId) {
          const deptKey = `${permCategory}_dept`;
          const userDept = userEffectivePermissions[deptKey];
          return userDept === options.departmentId;
        }
      }
      
      return true;
    }
    
    // For legacy frontend permission keys (canManageUsers, canViewDepartments, etc.)
    // Always use role-based permissions from getPermissions()
    const permissions = getPermissions();
    return permissions[permission] || false;
  };

  // Get organization-scoped data filter
  const getOrgFilter = () => {
    if (isIndividualUser || !currentOrganization) {
      return { userId: currentUser?._id }; // Individual user scope
    }
    
    return { organizationId: currentOrganization._id }; // Organization scope
  };

  // Clear error
  const clearError = () => setError(null);

  const value = {
    currentOrganization,
    availableOrganizations,
    departments,
    dashboardStats, // Expose stats
    loading,
    error,
    isEnterpriseUser,
    isIndividualUser,
    isPlatformAdmin,
    isOrgAdmin,
    orgAdminAccessLevel,
    isReadOnlyOrgAdmin,
    isLimitedOrgAdmin,
    isDeptManager,
    isDeptManagerByRole,
    isTeamLead,
    hasDepartmentAccess,
    userDepartmentId: currentUser?.departmentId, // Expose user's assigned department
    departmentRole,
    loadOrganizationData,
    refreshOrganizationData, // Expose refresh function
    refreshDashboardStats,
    switchOrganization,
    createOrganization,
    updateOrganization,
    createDepartment,
    getPermissions,
    hasPermission,
    getOrgFilter,
    clearError,
    // Custom permissions integration
    userEffectivePermissions,
    permissionsLoading,
    refreshPermissions: fetchUserEffectivePermissions
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export default OrganizationContext;
